/**
 * Sync Service API client.
 * Communicates with the edge sync-service via the api-gateway proxy (/api/sync/*).
 */
import api from './api';

export interface SyncStatus {
  is_online: boolean;
  last_push_at: string | null;
  last_pull_at: string | null;
  last_push_status: string | null;
  last_pull_status: string | null;
  pending_count: number;
  failed_count: number;
  conflict_count: number;
  consecutive_failures: number;
  uptime: number;
}

/**
 * Failover "brain" state from GET /sync/target (WCO-3793). `enabled: false` when failover is off on this edge.
 * `alert = 'edge_work_not_confirmed'` → changes made on the edge during an internet outage are not confirmed on the
 * cloud (replication slot missing, or the app went back to the cloud after the maximum wait).
 */
export interface FailoverStatus {
  enabled: boolean;
  target?: 'cloud' | 'edge' | 'unknown';
  alert?: 'edge_work_not_confirmed' | null;
  catchUpProblem?: string | null;
  unsentEdgeWork?: { since: string; reason: string } | null;
}

export interface SyncQueueEntry {
  id: string;
  table_name: string;
  natural_key: string;
  operation: string;
  changed_data: Record<string, any>;
  sync_status: string;
  error_message: string | null;
  created_at: string;
  synced_at: string | null;
}

export interface SyncQueuePage {
  items: SyncQueueEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const syncService = {
  /** Get current sync status */
  getStatus: async (): Promise<SyncStatus> => {
    const { data } = await api.get('/sync/status');
    return data;
  },

  /** Get the failover brain's state (which DB the app uses; whether offline work is confirmed on the cloud) */
  getFailover: async (): Promise<FailoverStatus> => {
    const { data } = await api.get('/sync/target');
    return data;
  },

  /** Force an immediate push+pull cycle */
  trigger: async () => {
    const { data } = await api.post('/sync/trigger');
    return data;
  },

  /** Get paginated queue entries */
  getQueue: async (params: {
    status: string;
    table?: string;
    page?: number;
    limit?: number;
  }): Promise<SyncQueuePage> => {
    const { data } = await api.get('/sync/queue', { params });
    return data;
  },

  /** Retry a single failed/conflict entry */
  retry: async (id: string) => {
    const { data } = await api.post(`/sync/queue/${id}/retry`);
    return data;
  },

  /** Dismiss a single failed/conflict entry */
  dismiss: async (id: string) => {
    const { data } = await api.post(`/sync/queue/${id}/dismiss`);
    return data;
  },

  /** Bulk retry multiple entries */
  bulkRetry: async (ids: string[]) => {
    const { data } = await api.post('/sync/queue/bulk-retry', { ids });
    return data;
  },

  /** Bulk dismiss multiple entries */
  bulkDismiss: async (ids: string[]) => {
    const { data } = await api.post('/sync/queue/bulk-dismiss', { ids });
    return data;
  },
};

export default syncService;
